import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';
import Image from 'next/image';
import { ResponsiveTypography } from '../Typography/v3/ResponsiveTypography';
import { Separator } from '../Layout/Landing/Content/Separator';
import { formatDateStringForSEO } from '@doltech/utils/lib/date';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { colorsV2 } from '../colors-v2';
import { getLengthEnumTextByKey } from '@doltech/utils/lib/enum/LengthEnum';
import { imgProxyLoader } from '@doltech/utils/lib/image-loader';

const Main = styled.div`
  display: grid;
  grid-gap: 16px;

  ${fromScreen(458)} {
    grid-template-columns: 1fr max-content;
  }
  .post-info-row {
    display: flex;
    align-items: center;
    .author {
      display: flex;
      align-items: center;
      width: 40px;
      height: 40px;
      position: relative;
      border-radius: 50%;
      margin-right: 8px;
    }

    .author-info {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      .author-name {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
        color: ${colorsV2.black100};
      }
    }

    .publish-n-read-time-row {
      display: flex;
      align-items: center;
      .published-date,
      .reading-time {
        color: ${colorsV2.black80};
      }
    }
  }

  .social-buttons-wrapper {
    display: flex;
    min-width: 130px;
    min-height: 28px;
    order: -1;
    ${fromScreen(458)} {
      justify-content: flex-end;
      order: 2;
    }
    &.empty {
      background-color: red;
    }
  }
`;

const BlogPostInfoMain = styled.div`
  display: none;
  ${fromScreen(480)} {
    display: block;
  }
`;

const BlogPostInfo = ({ publishedDate, readTimeInMinutes, blogLength }) => {
  const isEmpty = !publishedDate && !readTimeInMinutes && !blogLength;
  if (isEmpty) return null;
  return (
    <BlogPostInfoMain className="publish-n-read-time-row">
      {publishedDate && (
        <ResponsiveTypography.Paragraph className="published-date" variant="regular/14">
          {formatDateStringForSEO(publishedDate)}
        </ResponsiveTypography.Paragraph>
      )}
      {readTimeInMinutes && (
        <>
          <Separator mx="8px" variant="dot" />{' '}
          <div className="reading-time">
            <ResponsiveTypography.Text variant="regular/14">
              {isNaN(readTimeInMinutes) ? readTimeInMinutes : `${readTimeInMinutes} min read`}
            </ResponsiveTypography.Text>
          </div>
        </>
      )}
      {blogLength && (
        <>
          <Separator mx="8px" variant="dot" />{' '}
          <div className="reading-time">
            <ResponsiveTypography.Text variant="regular/14">
              {getLengthEnumTextByKey(blogLength)}
            </ResponsiveTypography.Text>
          </div>
        </>
      )}
    </BlogPostInfoMain>
  );
};

export const BlogCommonAuthor = ({
  socials,
  readTimeInMinutes = undefined,
  publishedDate,
  author,
  blogLength = undefined,
  className = undefined,
}) => {
  const { name: authorName = '...', avatarUrl } = author;

  return (
    <Main className={cl('author-n-social', className)}>
      <div className="post-info-row">
        <div className="author">
          <Image loader={imgProxyLoader} layout="fill" src={avatarUrl} alt={authorName} />
        </div>
        <div className="author-info">
          <ResponsiveTypography.Text className="author-name">
            {authorName}
          </ResponsiveTypography.Text>
          <BlogPostInfo
            blogLength={blogLength}
            publishedDate={publishedDate}
            readTimeInMinutes={readTimeInMinutes}
          />
        </div>
      </div>

      <div className={cl('social-buttons-wrapper', { empty: !socials })}>{socials}</div>
    </Main>
  );
};
